import { Component } from '@angular/core'
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms'
import { Router, RouterLink } from '@angular/router'
import { BehaviorSubject } from 'rxjs'
import { AuthService } from '../auth.service'
import { MatFormField, MatLabel, MatInput, MatPrefix } from '@angular/material/input'
import { AsyncPipe } from '@angular/common'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatIcon } from '@angular/material/icon'

@Component({
    selector: 'app-login',
    template: `
        <div>
            <h1 class="text-2xl font-bold text-gray-900 mb-1">Welcome back</h1>
            <p class="text-sm text-gray-500 mb-8">Sign in to your account to continue</p>

            <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-1">
                <mat-form-field class="w-full" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>Email</mat-label>
                    <mat-icon matPrefix class="mr-2 text-gray-400">email</mat-icon>
                    <input matInput formControlName="email" type="email" autocomplete="email" />
                </mat-form-field>
                <mat-form-field class="w-full" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>Password</mat-label>
                    <mat-icon matPrefix class="mr-2 text-gray-400">lock</mat-icon>
                    <input matInput formControlName="password" type="password" autocomplete="current-password" />
                </mat-form-field>

                @if (error$ | async; as error) {
                    <div class="flex items-center gap-2 text-danger text-sm mb-2 p-3 bg-red-50 rounded-lg">
                        <mat-icon class="!text-base !w-4 !h-4">error_outline</mat-icon>
                        {{ error }}
                    </div>
                }

                <button
                    mat-flat-button
                    type="submit"
                    class="!h-12 !rounded-lg w-full bg-btn-primary text-btn-primary-text"
                    [disabled]="loading$ | async">
                    @if (loading$ | async) {
                        <mat-spinner [diameter]="20" class="inline-block mr-2" />
                    }
                    Sign in
                </button>
            </form>

            <div class="mt-6 text-center">
                <a routerLink="/password-reset" class="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    Forgot password?
                </a>
            </div>
        </div>
    `,
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatPrefix,
        MatIcon,
        MatButton,
        MatProgressSpinner,
        RouterLink,
        AsyncPipe,
    ],
})
export class LoginComponent {
    form = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [Validators.required]),
    })

    loading$ = new BehaviorSubject(false)
    error$ = new BehaviorSubject<string | null>(null)

    constructor(
        private auth: AuthService,
        private router: Router,
    ) {}

    onSubmit() {
        if (this.form.invalid) return

        this.loading$.next(true)
        this.error$.next(null)

        const { email, password } = this.form.value
        this.auth.login(email!, password!).subscribe({
            next: () => {
                this.loading$.next(false)
                this.router.navigateByUrl('/')
            },
            error: (err) => {
                this.loading$.next(false)
                this.error$.next(err?.message || 'Login failed. Please try again.')
            },
        })
    }
}
